/**
 * Properties API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import { Property, Portfolio } from '@repo/shared-types';
import type { Note } from '@repo/shared-types/common/notes';
import { apiTransport as router } from './transport';
import { getContext } from './core';

export const propertiesApi = {
  list: async (filters?: Record<string, string>) => {
    const context = getContext();
    return router.get<Property[] | { data: Property[]; total: number }>('/properties', context, Object.keys(filters ?? {}).length ? filters : undefined);
  },

  get: async (id: string) => {
    const context = getContext();
    return router.get<Property>(`/properties/${id}`, context);
  },

  listFloorPlans: async (propertyId: string) => {
    const context = getContext();
    return router.get<Array<{ id: string; name: string; bed: number | null; bath: number | null }>>(
      `/properties/${propertyId}/floorplans`,
      context,
    );
  },

  create: async (property: Property) => {
    const context = getContext();
    return router.post<Property>('/properties', property, context);
  },

  update: async (id: string, updates: Partial<Property>) => {
    const context = getContext();
    return router.patch<Property>(`/properties/${id}`, updates, context);
  },

  delete: async (id: string) => {
    const context = getContext();
    return router.delete<{ success: boolean }>(`/properties/${id}`, context);
  },

  /** PM releases management to owner (isSelfManaged true, propertyManagerId null). */
  transferToOwner: async (id: string) => {
    const context = getContext();
    return router.patch<Property>(`/properties/${id}/transfer-to-owner`, {}, context);
  },

  /** Owner/PM assigns a property manager (propertyManagerId or inviteCode). */
  assignPm: async (id: string, body: { propertyManagerId?: string; inviteCode?: string }) => {
    const context = getContext();
    return router.patch<Property>(`/properties/${id}/assign-pm`, body, context);
  },

  portfolios: {
    list: async () => {
      return router.get<Portfolio[]>('/portfolios');
    },
    get: async (id: string) => {
      return router.get<Portfolio>(`/portfolios/${id}`);
    },
    create: async (portfolio: { name: string; description?: string; propertyIds: string[] }) => {
      return router.post<Portfolio>('/portfolios', portfolio);
    },
    update: async (id: string, updates: Partial<Portfolio>) => {
      const context = getContext();
      return router.patch<Portfolio>(`/portfolios/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/portfolios/${id}`, context);
    }
  },

  notes: {
    list: async (propertyId: string) => {
      return router.get<Note[]>(`/properties/${propertyId}/notes`);
    },
    create: async (propertyId: string, note: { content: string; category?: string; isImportant?: boolean }) => {
      const context = getContext();
      return router.post<Note>(`/properties/${propertyId}/notes`, note, context);
    },
    update: async (propertyId: string, noteId: string, updates: Partial<Pick<Note, 'content' | 'category' | 'isImportant'>>) => {
      return router.patch<Note>(`/properties/${propertyId}/notes/${noteId}`, updates);
    },
    delete: async (propertyId: string, noteId: string) => {
      return router.delete<{ success: boolean }>(`/properties/${propertyId}/notes/${noteId}`);
    }
  }
};

